/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ANALYTICS_SRC, ANALYTICS_TYPE, ERROR_MESSAGES, GLOBAL_EVENTS, LAYER_PLACEMENT, VIEW } from '../enums';
import { DATA_ATTRIBUTES, SCRIPT_ID } from '../constants';
import {
  FIRST_LAYER_VARIANT,
  InitOptions,
  SECOND_LAYER_VARIANT,
  TRACKING_EVENT_TYPE,
  TrackEventParameters,
} from '@usercentrics/cmp-browser-sdk';

declare global {
  interface Window {
    UC_AB_VARIANT?: string;
  }
}

export type AnalyticsEventParameters = {
  abTestVariant?: string;
  source: ANALYTICS_SRC;
  type: ANALYTICS_TYPE;
};

export type InitParameters = {
  options: InitOptions;
  settingsId: string;
};

type FormattedLegalDescription = {
  bulletPoints: string[];
  title: string;
};

type AMPClientConfig = {
  id: string;
  baseHref: string | '';
};

type AMPInitOptions = {
  clientConfig: AMPClientConfig;
};

export const getAmpClientConfig = (): AMPClientConfig | null => {
  try {
    // eslint-disable-next-line no-undef
    const { clientConfig } = JSON.parse(window.name) as AMPInitOptions;

    return clientConfig;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(ERROR_MESSAGES.CLIENT_CONFIG_NOT_AVAILABLE);
  }

  return null;
};

export const getInitParameters = (): InitParameters => {
  /**
   * Have to use document.getElementById instead of document.currentScript,
   * because currentScript has no support in IE11 and there's no polyfill for it.
   */
  const script = document.getElementById(SCRIPT_ID) as HTMLScriptElement;
  const hasScript = script && script.dataset;
  const isAmpEnabled = hasScript ? DATA_ATTRIBUTES.AMP_ENABLED in script.dataset : window[DATA_ATTRIBUTES.AMP_ENABLED];
  let settingsId = hasScript
    ? (script.dataset[DATA_ATTRIBUTES.SETTINGS_ID] as string)
    : window[DATA_ATTRIBUTES.SETTINGS_ID];

  // eslint-disable-next-line no-underscore-dangle
  if (!window.__webpack_nonce__ && script && script.nonce) {
    // inject nonce for styled components
    // eslint-disable-next-line no-underscore-dangle
    window.__webpack_nonce__ = script.nonce;
  }

  /**
   *  If amp is enabled, check for the usercentrics-amp clientConfig
   *  and extract the settings id
   * */
  if (isAmpEnabled) {
    const clientConfig = getAmpClientConfig();
    settingsId = clientConfig?.id || settingsId;
  }

  return {
    options: {
      ampEnabled: isAmpEnabled,
      blockDataLayerPush: hasScript && DATA_ATTRIBUTES.BLOCK_DATA_LAYER_PUSH in script.dataset,
      controllerId: hasScript && script.dataset[DATA_ATTRIBUTES.CONTROLLER_ID],
      createTcfApiStub: hasScript ? DATA_ATTRIBUTES.TCF_ENABLED in script.dataset : window[DATA_ATTRIBUTES.TCF_ENABLED],
      disableTracking: hasScript && DATA_ATTRIBUTES.DISABLE_TRACKING in script.dataset,
      euMode: hasScript && DATA_ATTRIBUTES.EU_MODE in script.dataset,
      language: hasScript ? script.dataset[DATA_ATTRIBUTES.LANGUAGE] : window[DATA_ATTRIBUTES.LANGUAGE],
      prefetchServices: hasScript ? !(DATA_ATTRIBUTES.AVOID_PREFETCH_SERVICES in script.dataset) : true,
      sandboxEnv: hasScript && DATA_ATTRIBUTES.SANDBOX_ENV in script.dataset,
      settingsCache: hasScript && script.dataset[DATA_ATTRIBUTES.SETTINGS_CACHE],
      suppressCmpDisplay:
        (hasScript && DATA_ATTRIBUTES.SUPPRESS_CMP_DISPLAY in script.dataset) ||
        window.UC_UI_SUPPRESS_CMP_DISPLAY === true,
      userCountryData: window.UC_UI_USER_COUNTRY_DATA || null,
      userSessionData: window.UC_UI_USER_SESSION_DATA || null,
      version: hasScript && script.dataset[DATA_ATTRIBUTES.VERSION],
    },
    settingsId,
  };
};

export const getFormatedLegalDescription = (legalDescription: string): FormattedLegalDescription[] | string => {
  const legalDescriptionNoWhitespaces = legalDescription.replace(/\s+/g, '');
  const formattedLines = legalDescription.match(/[^\r\n]+/g);

  /**
   * We check if we did not miss any characters from the legalText with our regex by comparing the length of the strings without whitespaces
   */
  const stringLengthsWithoutWhitespacesMatch =
    formattedLines?.reduce((result, line) => result + line.replace(/\s+/g, '').length, 0) ===
    legalDescriptionNoWhitespaces.length;

  if (!stringLengthsWithoutWhitespacesMatch) return legalDescription;

  const formattedDescriptions: FormattedLegalDescription[] = [{ bulletPoints: [], title: '' }];
  let counter = 0;

  if (formattedLines) {
    formattedLines.forEach((line, index) => {
      if (line.startsWith('*')) {
        formattedDescriptions[counter].bulletPoints.push(line.substr(1, 1) === ' ' ? line.substr(2) : line.substr(1));
      } else {
        if (index > 0) counter += 1;
        formattedDescriptions[counter] = { bulletPoints: [], title: line };
      }
    });

    /**
     * We check again if we did not miss any characters from the legalText with the object formatting by comparing the length of the strings without whitespaces and * (Unicode: 002a)
     */
    const stringLengthsWithoutWhitespacesAndStarsMatch =
      formattedDescriptions.reduce(
        (result, formDesc) =>
          result +
          formDesc.title.replace(/\s|\u002a+/g, '').length +
          formDesc.bulletPoints.reduce((bulletRes, bp) => bulletRes + bp.replace(/\s|\u002a+/g, '').length, 0),
        0,
      ) === legalDescriptionNoWhitespaces.replace(/\u002a+/g, '').length;

    if (stringLengthsWithoutWhitespacesAndStarsMatch) return formattedDescriptions;
  }

  return legalDescription;
};

export const isMailAddress = (mailString: string): boolean => {
  const regexCheck = /\S+@\S+\.\S+/;

  return regexCheck.test(mailString);
};

export const mapUIVariantToLayer = (
  layerVariant: FIRST_LAYER_VARIANT | SECOND_LAYER_VARIANT | null,
  testIds: { firstLayerWall: string; firstLayerBanner: string; secondLayer: string },
): { layerPlacement: LAYER_PLACEMENT | null; testId: string } => {
  switch (layerVariant) {
    case FIRST_LAYER_VARIANT.BANNER:
      return { layerPlacement: LAYER_PLACEMENT.BOTTOM, testId: testIds.firstLayerBanner };
    case FIRST_LAYER_VARIANT.WALL:
      return { layerPlacement: LAYER_PLACEMENT.CENTER, testId: testIds.firstLayerWall };
    case SECOND_LAYER_VARIANT.CENTER:
      return { layerPlacement: LAYER_PLACEMENT.CENTER, testId: testIds.secondLayer };
    case SECOND_LAYER_VARIANT.SIDE:
      return { layerPlacement: LAYER_PLACEMENT.SIDE, testId: testIds.secondLayer };
    default:
      return { layerPlacement: null, testId: '' };
  }
};

const isObject = (object: Record<string, any>): boolean => {
  return object != null && typeof object === 'object';
};

export const isEqual = (object1: Record<string, any>, object2: Record<string, any>): boolean => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);

    if ((areObjects && !isEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
};

export const dispatchAnalyticsEvent = (view: VIEW, type: ANALYTICS_TYPE, abTestVariant: string): void => {
  const source: ANALYTICS_SRC =
    view === VIEW.NONE || (view === VIEW.PRIVACY_BUTTON && type !== ANALYTICS_TYPE.CMP_SHOWN)
      ? ANALYTICS_SRC.API
      : (view as unknown as ANALYTICS_SRC);
  const detail: AnalyticsEventParameters = { source, type };

  if (abTestVariant && abTestVariant.length) {
    detail.abTestVariant = abTestVariant;
  }

  const analyticsEvent = new window.CustomEvent(GLOBAL_EVENTS.ANALYTICS, {
    detail,
  });
  window.dispatchEvent(analyticsEvent);
};

export const setTrackingPixel = (
  eventType: TRACKING_EVENT_TYPE,
  UCSetTrackingPixel: (trackEventParameters: TrackEventParameters) => void,
  interactionAnalyticsEnabled: boolean,
): void => {
  if (interactionAnalyticsEnabled) {
    const trackEventParameters: TrackEventParameters = {
      eventType,
    };
    UCSetTrackingPixel(trackEventParameters);
  }
};

/**
 * Remove all children from an HTML Element.
 * @param container An HTML Element.
 */
export const reset = (container: Element): void => {
  while (container.lastElementChild) {
    container.removeChild(container.lastElementChild);
  }
};
